import { Button, ButtonTheme } from 'components/Button';
import { ICONS } from 'components/Icon';
import { Txt } from 'components/Txt';
import { theme } from 'lib/theme';
import * as React from 'react';
import { Platform, ScrollView, View } from 'react-native';
import * as Linking from 'expo-linking';
import * as Clipboard from 'expo-clipboard';
import { formatDate, openUrl } from 'lib/riskbase';
import { CardRow } from 'components/CardRow';
import { Card } from 'components/Card';
import QRCode from 'react-native-qrcode-svg';
import { CodeField } from 'components/Field/CodeField';
import { SeparatorHorizontal } from 'components/SeparatorHorizontal';
import { CardGeneric } from 'components/CardGeneric';
import { BlurView } from 'expo-blur';
import { useAlert } from 'hooks/useAlert';
import { animateNext } from 'lib/animateNext';
import { API_DATE_FORMAT, UI_DATE_FORMAT_LONG } from 'lib/constants';
import { str } from 'lib/str';

type Props = {
  secret: string;
  uri: string;
  required: { date: number } | null;
  onSubmit: (code: string) => void;
  withScrollView: boolean;
};
export const TfaSetupComponent: React.FC<Props> = ({
  secret,
  uri,
  required,
  onSubmit,
  withScrollView,
}) => {
  const alert = useAlert();

  const [pastedVerificationCode, setPastedVerificationCode] =
    React.useState('');
  const [showSecret, setShowSecret] = React.useState(false);
  const [showQrCode, setShowQrCode] = React.useState(false);

  const handleAutomaticSetup = () => {
    Linking.openURL(uri);
  };

  const handleShowQrCode = () => {
    animateNext(() => setShowQrCode((ShowQrCodeC) => !ShowQrCodeC));
  };

  const handleCopySecret = async () => {
    await Clipboard.setStringAsync(secret);
    alert({
      title: 'Secret Copied',
      text: 'Paste it into your authenticator app',
    });
    setTimeout(() => {
      animateNext(() => setShowSecret(true));
    }, 200);
  };

  const handlePasteVerificationCode = async () => {
    const value = await Clipboard.getStringAsync();
    const cleanValue = value
      .split('')
      .filter((v) => Number.isInteger(parseInt(v, 10)))
      .join('')
      .slice(0, 6);
    if (cleanValue) {
      setPastedVerificationCode(cleanValue);
    } else {
      alert({ text: 'Sorry, there was no code to paste' });
    }
  };

  const handleVerificationCodeChange = (value: string) => {
    if (value.length === 6) {
      onSubmit(value);
    }
  };

  const handleShowRequiredAlert = () => {
    if (!required) {
      return;
    }
    if (
      formatDate(required.date, API_DATE_FORMAT) >
      formatDate(new Date(), API_DATE_FORMAT)
    ) {
      alert({
        title: 'Multi-Factor Authentication Required',
        text:
          'A setting on your account requires you enable MFA. ' +
          `You can skip setup until *${formatDate(
            required.date,
            UI_DATE_FORMAT_LONG,
          )}*\n\n` +
          `After ${formatDate(required.date, UI_DATE_FORMAT_LONG)} you${
            str.apos
          }ll be required to setup MFA to continue using RiskBase`,
      });
    } else {
      alert({
        title: 'Multi-Factor Authentication Required',
        text: 'A setting on your account requires you enable MFA. Please enable MFA on your account to continue using RiskBase',
      });
    }
  };

  const Form1 = (
    <>
      {!!required && (
        <Card style={{ marginBottom: theme.paddingVertical / 2 }}>
          <CardRow
            icon={ICONS.CircleInfoLight}
            label="MFA is required on your account"
            labelColor={theme.colors.red}
            iconColor={theme.colors.red}
            onPress={handleShowRequiredAlert}
          />
        </Card>
      )}
      <CardGeneric margin={false}>
        <Txt>
          <Txt weight="bold">
            Add an extra layer of security to your account.
          </Txt>{' '}
          Enable MFA and RiskBase will ask for a code generated by your MFA app
          every time you sign in
        </Txt>
        <Button
          text="Read more about Multi-Factor Authentication"
          buttonTheme={ButtonTheme.link}
          textSize="small"
          onPress={() =>
            openUrl(
              'https://riskbase.uk/support/app/profile#multi-factor-authentication',
            )
          }
          style={{
            marginHorizontal: -theme.paddingHorizontalHalf,
            alignSelf: 'flex-start',
          }}
        />
      </CardGeneric>
      <SeparatorHorizontal marginVertical={theme.paddingVertical} />
      <Card label="Choose an option below to setup your MFA app">
        {Platform.OS === 'ios' && (
          <CardRow
            label="Setup Automatically"
            onPress={handleAutomaticSetup}
            icon={ICONS.WandMagicSparklesLight}
          />
        )}
        <CardRow
          labelAlignTop
          label={showQrCode ? 'Hide QR Code' : 'Show QR Code'}
          value={
            showQrCode ? 'Scan this QR code with your authenticator app' : ''
          }
          onPress={handleShowQrCode}
          icon={ICONS.QRCodeLight}
        />
        {showQrCode && (
          <CardRow contentStyle={{ alignItems: 'center' }}>
            <QRCode value={uri} size={150} />
          </CardRow>
        )}
        <CardRow
          label={!showSecret ? 'Show and Copy Secret' : 'Secret Copied'}
          value={showSecret ? secret : ''}
          onPress={handleCopySecret}
          icon={ICONS.CopyLight}
          valueStyle={{ fontFamily: 'Courier New', fontSize: 13 }}
          labelAlignTop
        />
      </Card>
    </>
  );

  const Form2 = (
    <Card
      label="To finish setup, enter the verification code from your authenticator app"
      bodyStyle={{ borderColor: 'white', borderWidth: 1 }}
      labelLinkText="Paste"
      onLabelLinkPress={handlePasteVerificationCode}
      margin={false}
    >
      <CodeField
        onChange={handleVerificationCodeChange}
        rootStyle={{ backgroundColor: 'white' }}
        externalValue={pastedVerificationCode}
        isOTP
      />
    </Card>
  );

  if (withScrollView) {
    return (
      <>
        <ScrollView
          contentContainerStyle={{
            paddingTop: theme.paddingVertical,
            paddingBottom: 148,
          }}
          scrollIndicatorInsets={{ bottom: 148 }}
        >
          {Form1}
        </ScrollView>
        <BlurView
          intensity={withScrollView ? 10 : 0}
          style={{
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1,
            elevation: 1,
            borderTopWidth: 1,
            borderColor: '#ddd',
            height: 148,
            position: 'absolute',
          }}
        >
          <View
            style={{
              backgroundColor: theme.colors.background80,
              paddingVertical: theme.paddingVertical,
              borderBottomLeftRadius: 8,
              borderBottomRightRadius: 8,
            }}
          >
            {Form2}
          </View>
        </BlurView>
      </>
    );
  }

  return (
    <>
      {Form1}
      <SeparatorHorizontal withMarginBottom />
      {Form2}
    </>
  );
};
